import { FunctionComponent, useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Modal } from "@/components/Modals/Modal";
import { useAppSelector } from "@/redux/hooks";

import {
	useCreateLanguageRuleMutation,
	useDeleteLanguageRuleMutation,
	useGetDefaultLanguageQuery,
	useGetLanguageRulesQuery,
	useReorderLanguageRulesMutation,
	useSetDefaultLanguageMutation,
	useUpdateLanguageRuleMutation,
} from "@/pages/Private/redux/languageRule/languageRule.api";
import { languageRuleSelector } from "@/pages/Private/redux/languageRule/languageRule.slice";

import { LanguageRuleInfo } from "../../../../schema/languageRule";

import { LanguagePicker } from "./LanguagePicker";
import { LanguageRuleRow } from "./LanguageRuleRow";
import {
	buildReorderPayload,
	diffRules,
	DraftCondition,
	DraftRule,
	DraftFormSchema,
	makeEmptyCondition,
	makeEmptyRule,
	toDraft,
} from "./helpers";

interface LanguageAutoDetectRulesModalProps {
	isOpened: boolean;
	onClose: () => void;
}

interface ModalContentProps {
	isOpened: boolean;
	onClose: () => void;
	serverRules: LanguageRuleInfo[];
	serverDefault: string | null;
}

export const LanguageAutoDetectRulesModal: FunctionComponent<LanguageAutoDetectRulesModalProps> = ({
	isOpened,
	onClose,
}) => {
	const {
		rules: serverRules,
		defaultOutputLanguage: serverDefault,
		loaded,
	} = useAppSelector(languageRuleSelector);

	useGetLanguageRulesQuery();
	useGetDefaultLanguageQuery();

	return (
		<ModalContent
			key={isOpened && loaded ? "active" : "inactive"}
			isOpened={isOpened}
			onClose={onClose}
			serverDefault={serverDefault}
			serverRules={serverRules}
		/>
	);
};

const ModalContent: FunctionComponent<ModalContentProps> = ({
	isOpened,
	onClose,
	serverRules,
	serverDefault,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`messages.languageAutoDetectRules.${key}`);

	const [createRule] = useCreateLanguageRuleMutation();
	const [updateRule] = useUpdateLanguageRuleMutation();
	const [deleteRule] = useDeleteLanguageRuleMutation();
	const [reorderRules] = useReorderLanguageRulesMutation();
	const [setDefaultLang] = useSetDefaultLanguageMutation();

	const [draftRules, setDraftRules] = useState<DraftRule[]>(() => toDraft(serverRules));
	const [draftDefault, setDraftDefault] = useState<string>(() => serverDefault ?? "en");
	const [isSaving, setIsSaving] = useState(false);
	const [validationError, setValidationError] = useState<string | null>(null);

	const handleAddRule = useCallback(() => {
		setDraftRules((prev) => [...prev, makeEmptyRule()]);
		setValidationError(null);
	}, []);

	const handleRemoveRule = useCallback((uid: string) => {
		setDraftRules((prev) => prev.filter((r) => r.uid !== uid));
	}, []);

	const handleAddCondition = useCallback((ruleUid: string) => {
		setDraftRules((prev) =>
			prev.map((r) => {
				if (r.uid !== ruleUid) {
					return r;
				}

				return { ...r, conditions: [...r.conditions, makeEmptyCondition()] };
			})
		);
	}, []);

	const handleChangeCondition = useCallback(
		(ruleUid: string, conditionUid: string, updates: Partial<DraftCondition>) => {
			setDraftRules((prev) =>
				prev.map((r) => {
					if (r.uid !== ruleUid) {
						return r;
					}

					return {
						...r,
						conditions: r.conditions.map((c) => {
							if (c.uid !== conditionUid) {
								return c;
							}

							return { ...c, ...updates } as DraftCondition;
						}),
					};
				})
			);
		},
		[]
	);

	const handleRemoveCondition = useCallback((ruleUid: string, conditionUid: string) => {
		setDraftRules((prev) =>
			prev.map((r) => {
				if (r.uid !== ruleUid) {
					return r;
				}

				return { ...r, conditions: r.conditions.filter((c) => c.uid !== conditionUid) };
			})
		);
	}, []);

	const handleChangeOutputLanguage = useCallback((ruleUid: string, code: string) => {
		setDraftRules((prev) =>
			prev.map((r) => (r.uid === ruleUid ? { ...r, outputLanguage: code } : r))
		);
	}, []);

	const handleSave = useCallback(async () => {
		setValidationError(null);

		const parseResult = DraftFormSchema.safeParse({
			rules: draftRules,
			defaultOutputLanguage: draftDefault,
		});

		if (!parseResult.success) {
			const first = parseResult.error.issues[0];

			setValidationError(first?.message ?? "Validation failed");

			return;
		}

		setIsSaving(true);
		try {
			const { toCreate, toUpdate, toDelete } = diffRules(serverRules, draftRules);

			// 1. Delete removed rules
			await Promise.all(toDelete.map((id) => deleteRule(id).unwrap()));

			// 2. Update changed rules
			await Promise.all(toUpdate.map((u) => updateRule({ id: u.id, body: u.body }).unwrap()));

			// 3. Create new rules and capture their ids
			const newRuleIndices = draftRules
				.map((d, i) => ({ draftIndex: i, id: d.id }))
				.filter((x) => x.id === null)
				.map((x) => x.draftIndex);

			const created = await Promise.all(toCreate.map((payload) => createRule(payload).unwrap()));

			// Map draft positions to ids
			const idByDraftIndex = new Map<number, number>();

			draftRules.forEach((d, i) => {
				if (d.id !== null) {
					idByDraftIndex.set(i, d.id);
				}
			});

			newRuleIndices.forEach((draftIdx, i) => {
				idByDraftIndex.set(draftIdx, created[i].id);
			});

			// 4. Reorder all rules
			const allIds = Array.from({ length: draftRules.length }, (_, i) =>
				idByDraftIndex.get(i)
			).filter((id): id is number => id !== undefined);

			if (allIds.length > 0) {
				await reorderRules(buildReorderPayload(allIds)).unwrap();
			}

			// 5. Update default language if changed
			if (draftDefault !== (serverDefault ?? "en")) {
				await setDefaultLang({ defaultOutputLanguage: draftDefault }).unwrap();
			}

			onClose();
		} catch {
			setValidationError(ts("failedToSave"));
		} finally {
			setIsSaving(false);
		}
	}, [
		draftRules,
		draftDefault,
		serverRules,
		serverDefault,
		deleteRule,
		updateRule,
		createRule,
		reorderRules,
		setDefaultLang,
		onClose,
		t,
	]);

	return (
		<Modal
			handleClose={onClose}
			handleSave={handleSave}
			isLoading={isSaving}
			isOpened={isOpened}
			size="lg"
			submitButtonText={ts("save")}
			subtitle={ts("subtitle")}
			title={ts("title")}
			zIndex={10000}
		>
			<div className="flex flex-col gap-4">
				{validationError && (
					<div className="text-sm text-status-error bg-status-error-light p-2 rounded border border-status-error">
						{validationError}
					</div>
				)}
				<div className="flex flex-col gap-3">
					{draftRules.map((rule, index) => (
						<LanguageRuleRow
							index={index}
							key={rule.uid}
							onAddCondition={() => handleAddCondition(rule.uid)}
							onChangeCondition={(cid, updates) => handleChangeCondition(rule.uid, cid, updates)}
							onChangeOutputLanguage={(code) => handleChangeOutputLanguage(rule.uid, code)}
							onRemoveCondition={(cid) => handleRemoveCondition(rule.uid, cid)}
							onRemoveRule={() => handleRemoveRule(rule.uid)}
							rule={rule}
						/>
					))}
				</div>
				<div className="flex flex-col gap-2 p-4 rounded-lg border border-border-light bg-brand-white">
					<div className="flex items-center gap-2">
						<span className="text-sm text-text-meta">{ts("elseLabel")}</span>
						<LanguagePicker onChange={setDraftDefault} value={draftDefault} />
					</div>
				</div>
				<div className="flex items-center gap-2">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.S}
						title={ts("addRule")}
						onClick={handleAddRule}
					/>
				</div>
			</div>
		</Modal>
	);
};
